import React from 'react';

type MessageGuardApi = {
  arm: (ms?: number) => void;
  status: () => { armed: boolean; allow: string[] };
  allowAdd: (origin: string) => void;
  allowDel: (origin: string) => void;
};

declare global {
  interface Window {
    __MESSAGE_GUARD__?: MessageGuardApi;
    __DIAG_MARK__?: (label?: string, windowMs?: number) => void;
  }
}

type Cfg = {
  allowOrigins?: string[];   // e.g. ["https://venue-webapp-ai.vercel.app"]
  windowMs?: number;         // default 1400ms
};

export default function MessageGuard({ allowOrigins = [], windowMs = 1400 }: Cfg) {
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const selfOrigin = window.location.origin;
    const allow = new Set<string>(
      [selfOrigin, ...allowOrigins.map((s) => s.trim()).filter(Boolean)]
    );

    let until = 0;

    const arm = (ms: number = windowMs) => {
      // arm for ms from now
      until = performance.now() + Math.max(200, ms);
    };

    const status = () => ({ armed: performance.now() < until, allow: Array.from(allow) });
    const allowAdd = (origin: string) => allow.add(origin);
    const allowDel = (origin: string) => allow.delete(origin);

    const onMessageCapture = (e: MessageEvent) => {
      // Only guard during the hot window after an Edit click
      if (performance.now() >= until) return;

      const origin = e.origin || 'null';
      if (!allow.has(origin)) {
        // Drop heavy cross-origin postMessages that have caused long handlers
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener('message', onMessageCapture, true);
    window.__MESSAGE_GUARD__ = { arm, status, allowAdd, allowDel };

    return () => {
      window.removeEventListener('message', onMessageCapture, true);
      if (window.__MESSAGE_GUARD__) delete window.__MESSAGE_GUARD__;
    };
  }, [allowOrigins, windowMs]);

  return null;
}
